/**
 * @description Requests shared between apps
 */
import {
  AppsInfo as AppsInfoAPIResponse,
  Cart,
  Offer,
  Product,
  SmartProduct,
} from '../types';
import {
  ajaxApiRequestConfig,
  getCurrentLocale,
  getCurrentLocalePrefix,
  handleResponseDeprecated,
  parseJsonDeprecated,
  timeoutFetch,
} from './utils';
import {
  APIError,
  IGNORE_TAGS,
  NetworkError,
  RecommendedProductsRequestConfig,
} from './types';
import { logInfo } from '../utils/logging';

function getProduct(handle: string) {
  const path = window.location.pathname;
  const localePrefix = getCurrentLocalePrefix(path);

  return parseJsonDeprecated(fetch(`${localePrefix}/products/${handle}.js`));
}

function getCart() {
  return parseJsonDeprecated(
    fetch(`/cart.js?_=${new Date().getTime()}`),
  ) as Promise<Cart>;
}

function addToCart(
  productVariantId: number,
  quantity: number,
  properties?: Record<string, string> | null,
) {
  const data: any = {
    quantity,
    id: productVariantId,
  };

  if (properties) {
    data.properties = properties;
  }

  return parseJsonDeprecated(
    fetch(`/cart/add.js`, {
      ...ajaxApiRequestConfig,
      body: JSON.stringify(data),
    }),
  );
}

function removeFromCart(productVariantId: number | string, qty?: number) {
  const data = {
    quantity: qty ? qty : 0,
    id: String(productVariantId),
  };

  return parseJsonDeprecated(
    fetch(`/cart/change.js`, {
      ...ajaxApiRequestConfig,
      body: JSON.stringify(data),
    }),
  );
}

function distributeOffersToSmartProducts(
  products: Product[],
  smartOffers: Offer[],
): SmartProduct[] {
  let indexOffers = 0;
  let indexProducts = 0;
  return products.map((product) => {
    const productWithOffer = {
      ...product,
      offer: smartOffers[indexOffers],
    };
    if (
      indexProducts + 1 ===
      smartOffers[indexOffers].recommended_product_count
    ) {
      indexProducts = 0;
      indexOffers++;
    } else {
      indexProducts++;
    }
    return productWithOffer;
  });
}

function changeItems(productsToRemove: any) {
  const data = { updates: productsToRemove };
  return parseJsonDeprecated(
    fetch(`/cart/update.js`, {
      ...ajaxApiRequestConfig,
      body: JSON.stringify(data),
    }),
  );
}

function updateAttributes(attributes: any) {
  const data = {
    attributes,
  };

  return fetch(`/cart/update.js`, {
    ...ajaxApiRequestConfig,
    body: JSON.stringify(data),
  });
}

async function getAppsInfo(): Promise<
  AppsInfoAPIResponse | APIError | NetworkError
> {
  // REACT_APP_CUSTOMER_API_HOSTNAME will be empty string in the forked application
  const hostname = process.env.REACT_APP_CUSTOMER_API_HOSTNAME?.trim()?.length
    ? process.env.REACT_APP_CUSTOMER_API_HOSTNAME
    : process.env.REACT_APP_LAST_UPSELL_API_HOSTNAME;
  const shop = window.Shopify.shop;
  const response = await handleResponseDeprecated(
    fetch(`${hostname}api/apps-info/?shop=${shop}`),
  );

  return response as Promise<AppsInfoAPIResponse>;
}

async function getRecommendedProductsBase(
  productId: number,
  config: RecommendedProductsRequestConfig,
  ignoreTag: IGNORE_TAGS,
  limit: number,
) {
  // Will return empty string if window.CANDYRACK_DISABLE_LOCALE === true
  const localePrefix = getCurrentLocalePrefix(window.location.pathname);
  try {
    const response = await parseJsonDeprecated(
      timeoutFetch(
        `${localePrefix}/recommendations/products.json?product_id=${productId}&limit=${limit}`,
        3000,
      ),
    );
    const recommendedProducts = response?.products?.filter(
      (product: Product) => !product.tags.includes(ignoreTag),
    );
    if (recommendedProducts?.length > 0) {
      return recommendedProducts;
    } else {
      // Will return empty string if window.CANDYRACK_DISABLE_LOCALE === true
      const locale = getCurrentLocale(window.location.pathname);
      return await parseJsonDeprecated(
        fetch(
          `${config.hostname}api/${config.appPrefix}/smart-upsell-fallback?shop=${window.Shopify.shop}&lang=${locale}`,
        ),
      );
    }
  } catch (e) {
    logInfo(e);
    return [];
  }
}

export {
  updateAttributes,
  changeItems,
  removeFromCart,
  addToCart,
  getCart,
  getProduct,
  getAppsInfo,
  getRecommendedProductsBase,
  distributeOffersToSmartProducts,
};
